import { faCat, faDog, faPlus, faUser, IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import Chip from '@material-ui/core/Chip';
import { searchCustomersThunk } from 'actions/autocomplete/AutocompleteActions';
import { clearClientsThunk } from '@spike/clients-action';
import { clearPetsThunk } from '@spike/pets-action';
import clsx from 'clsx';
import { TextFieldNoLabel } from 'components/UI';
import AutocompleteResult from 'model/AutocompleteResult';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AutocompleteState, AutocompleteStatus } from 'reducers/autocomplete/AutocompleteState';
import { RootState } from 'store';
import { useCommonStyles } from 'components/UI/CommonStyles';
import { useSearchClientStyles } from 'components/Payments/SingleSale/SearchClient/SearchClient.styles';
import useNonInitialEffect from '@versiondos/hooks';

interface SearchClientProps {
    onAddClient: () => void;
    onSelectClient: (clientID: number | undefined) => void;
    parentName?: string;
    className?: string;
}

export const SearchClient: FunctionComponent<SearchClientProps> = props => {
    const dispatch = useDispatch();
    const classes = useSearchClientStyles();
    const commonClasses = useCommonStyles();

    const [parentName, setParentName] = useState<string | undefined>(props.parentName);
    const [showSearchResult, setShowSearchResult] = useState(false);
    const [showSearchField, setShowSearchField] = useState(!props.parentName);
    const [searchingParent, setSearchingParent] = useState(false);
    const [resultParent, setResultParent] = useState(true);

    const parent = useSelector<RootState, AutocompleteState>(state => state.autocomplete);
    const parents = useSelector<RootState, Array<AutocompleteResult>>(state => state.autocomplete.results);

    const iconSVG = (icon: IconDefinition, styles: string) => {
        return (
            <FontAwesomeIcon
                icon={icon}
                className={styles}
            />
        );
    };

    useEffect(() => {
        if (parent.status === AutocompleteStatus.SearchingSuccess) {
            setSearchingParent(false);
            parents.length === 0 ? setResultParent(false) : setResultParent(true);
        }

        if (parent.status !== AutocompleteStatus.SearchingSuccess) {
            setSearchingParent(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parent.status]);

    useNonInitialEffect(() => {
        if (props.parentName) {
            setParentName(props.parentName);
            setShowSearchField(false);
            setShowSearchResult(false);
            return;
        }

        handlerClearSearch();
    }, [props.parentName]);

    const changeHandler = (value: string, name: string) => {
        setParentName(value);
        setShowSearchResult(true);

        if (value === '') {
            setShowSearchResult(false);
        } else {
            dispatch(searchCustomersThunk(value));
        }
    };

    const handlerClient = (value: AutocompleteResult) => {
        setShowSearchResult(false);
        setShowSearchField(false);
        setParentName(value.name);
        props.onSelectClient(value.id);
    };

    const camelize = (str: string) => {
        return str.replace(/\b(\w)/g, s => s.toUpperCase());
    };

    const handlerClearSearch = () => {
        setParentName('');
        setShowSearchField(true);
        setShowSearchResult(false);
        //
        props.onSelectClient(undefined);
    };

    const addNewClient = () => {
        props.onAddClient();
        dispatch(clearClientsThunk());
        dispatch(clearPetsThunk());
    };

    const searchIconType = (parent: AutocompleteResult) => {
        let icon;

        switch (parent.type) {
            case 'Customer':
                icon = iconSVG(faUser, `${classes.sectionIcon} ${classes.listAlignedIcons}`);
                break;
            case 'Pet':
                switch (parent.petType?.singular) {
                    case 'Cat':
                        icon = iconSVG(faCat, `${classes.sectionIcon} ${classes.listAlignedIcons}`);
                        break;
                    default:
                        icon = iconSVG(faDog, `${classes.sectionIcon} ${classes.listAlignedIcons}`);
                        break;
                }
                break;
        }

        return icon;
    };

    const loadingView = (
        <Box className={classes.boxLoading}>
            <CircularProgress
                size="1.5rem"
                className={classes.loading}
            />
        </Box>
    );

    const searchFieldView = (
        <>
            <Box className={clsx(commonClasses.flexRow, classes.boxTitle)}>
                <Typography className={clsx(commonClasses.typo16_400_uppercase, classes.desktop)}>Client</Typography>
                <Typography className={clsx(classes.parentTitle, classes.mobile)}>Client</Typography>
            </Box>
            <TextFieldNoLabel
                placeholder="Search by email or client name"
                name="name"
                value={parentName ? parentName : ''}
                className={clsx(classes.field, {
                    [classes.fieldSearch]: showSearchResult
                })}
                onChange={changeHandler}
                autoComplete="off"
            />
        </>
    );

    const parentSelectedView = (
        <Box>
            <Box className={clsx(classes.boxTitle, classes.mobile)}>
                <Typography className={classes.parentTitle}>Client</Typography>
            </Box>
            <Chip
                label={parentName}
                variant="default"
                className={classes.chipParent}
                //
                onDelete={handlerClearSearch}
            />
        </Box>
    );

    const clientLinkView = (
        <Box className={classes.addNewButton}>
            <Box
                onClick={() => addNewClient()}
                className={clsx(classes.clientLink, commonClasses.flexRow)}
            >
                {iconSVG(faPlus, `${classes.sectionIcon} ${classes.listAlignedIcons}`)}{' '}
                <Typography className={commonClasses.typo16_500}>Add New Client</Typography>
            </Box>
        </Box>
    );
    //

    return (
        <Box className={props.className}>
            {showSearchField && searchFieldView}
            {!showSearchField && parentSelectedView}
            {showSearchResult && (
                <Box className={classes.wrapperResult}>
                    <Box className={classes.boxResult}>
                        {searchingParent && loadingView}
                        {!searchingParent && !resultParent && <Box>Pet parent no found</Box>}
                        {!searchingParent &&
                            resultParent &&
                            parents.map((parent, index) => (
                                <Box
                                    key={parent.id + parent.name}
                                    className={classes.boxItem}
                                    onClick={() => handlerClient(parent)}
                                >
                                    {searchIconType(parent)} {camelize(parent.name)}
                                </Box>
                            ))}
                    </Box>
                </Box>
            )}
            {!parentName && clientLinkView}
        </Box>
    );
};
